import clsx from 'clsx'
import { useState } from 'react'

import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { Checkbox } from './fields/Checkbox'
import { Select } from './fields/Select'
import { SelectOption, availabilityHours } from '../settings/Options'

const DEFAULT_START = { value: '9', label: '9:00am' }
const DEFAULT_END = { value: '18', label: '6:00pm' }

export const MON_SCHEDULE = 0x1
export const TUE_SCHEDULE = 0x2
export const WED_SCHEDULE = 0x4
export const THU_SCHEDULE = 0x8
export const FRI_SCHEDULE = 0x10
export const SAT_SCHEDULE = 0x20
export const SUN_SCHEDULE = 0x40
export const FULL_SCHEDULE = 0x7f

export type ScheduleForm = {
    monStart?: number
    monEnd?: number
    tueStart?: number
    tueEnd?: number
    wedStart?: number
    wedEnd?: number
    thuStart?: number
    thuEnd?: number
    friStart?: number
    friEnd?: number
    satStart?: number
    satEnd?: number
    sunStart?: number
    sunEnd?: number
    schedule: number
}

function dayIsEnabled(day: number, schedule: number | undefined) {
    if (!schedule) return false
    return Boolean(schedule & day)
}

function toggleDayEnabled(day: number, schedule: number, enable: boolean) {
    if (enable) {
        return schedule | day
    }
    return (FULL_SCHEDULE ^ day) & schedule
}

function getHourOption(h: number | undefined, _default: SelectOption) {
    return availabilityHours.find((a) => a.value === h?.toString()) ?? _default
}

function getStartHourOption(h: number | undefined) {
    return getHourOption(h, DEFAULT_START)
}
function getEndHourOption(h: number | undefined) {
    return getHourOption(h, DEFAULT_END)
}

function SingleDaySchedule({
    isEnabled,
    onToggle,
    day,
    start,
    onStartChange,
    end,
    onEndChange,
}: {
    isEnabled: boolean
    onToggle: VoidFunction
    start: SelectOption
    onStartChange: (start: SelectOption) => void
    end: SelectOption
    onEndChange: (end: SelectOption) => void
    day: string
}) {
    const [isStartOpen, setIsStartOpen] = useState(false)
    const [isEndOpen, setIsEndOpen] = useState(false)

    const isError =
        (isEnabled && !start.value && !end.value) ||
        (isEnabled && start.value && end.value && Number(start.value) >= Number(end.value))

    return (
        <div
            tabIndex={-1} // necessary for onBlur, but we don't want the div to have a tab index
            className={clsx(
                'flex h-20 items-center justify-between rounded-lg p-[15px]',
                'cursor-pointer hover:bg-black/4 active:bg-black/8',
                'border-1 border-gray-300',
                { 'outline-secondary-400 outline-2': isEnabled }
            )}
            onClick={onToggle}
        >
            <div className="flex gap-4">
                <Checkbox isChecked={isEnabled} onChange={onToggle} />
                <Label>{day}</Label>
            </div>
            {isEnabled ? (
                <div className="flex items-center gap-2">
                    <Select
                        size="medium"
                        options={availabilityHours.map((h) => ({
                            id: h.value,
                            title: h.label,
                            value: h.value,
                        }))}
                        selectedOptionID={start.value}
                        onSelectOption={(option) =>
                            onStartChange({ value: option.value, label: option.title })
                        }
                        shouldRestrictContentToTriggerWidth={false}
                        // onChange={(value) => onStartChange({ value, label: value ? availabilityHours.find(h => h.value === value)?.label || '' : '' })}
                        className={isError ? 'border-danger-400 border' : undefined}
                        // placeholder={start.label}
                    />
                    <Paragraph>-</Paragraph>
                    <Select
                        size="medium"
                        options={availabilityHours.map((h) => ({
                            id: h.value,
                            title: h.label,
                            value: h.value,
                        }))}
                        selectedOptionID={end.value}
                        onSelectOption={(option) =>
                            onEndChange({ value: option.value, label: option.title })
                        }
                        className={isError ? 'border-danger-400 border' : undefined}
                    />
                </div>
            ) : (
                <Paragraph className="text-gray-600">Unavailable</Paragraph>
            )}
        </div>
    )
}

export function HourForm({
    scheduleForm,
    onUpdateScheduleForm,
}: {
    scheduleForm: ScheduleForm
    onUpdateScheduleForm: (form: ScheduleForm) => void
}) {
    return (
        <form className="flex flex-col gap-3">
            <SingleDaySchedule
                day={'Mon'}
                isEnabled={dayIsEnabled(MON_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.monStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        monStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.monEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        monEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(MON_SCHEDULE, scheduleForm.schedule)
                    const monStart = enabled
                        ? (scheduleForm.monStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const monEnd = enabled
                        ? (scheduleForm.monEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        monStart,
                        monEnd,
                        schedule: toggleDayEnabled(MON_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Tue'}
                isEnabled={dayIsEnabled(TUE_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.tueStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        tueStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.tueEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        tueEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(TUE_SCHEDULE, scheduleForm.schedule)
                    const tueStart = enabled
                        ? (scheduleForm.tueStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const tueEnd = enabled
                        ? (scheduleForm.tueEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        tueStart,
                        tueEnd,
                        schedule: toggleDayEnabled(TUE_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Wed'}
                isEnabled={dayIsEnabled(WED_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.wedStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        wedStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.wedEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        wedEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(WED_SCHEDULE, scheduleForm.schedule)
                    const wedStart = enabled
                        ? (scheduleForm.wedStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const wedEnd = enabled
                        ? (scheduleForm.wedEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        wedStart,
                        wedEnd,
                        schedule: toggleDayEnabled(WED_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Thu'}
                isEnabled={dayIsEnabled(THU_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.thuStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        thuStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.thuEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        thuEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(THU_SCHEDULE, scheduleForm.schedule)
                    const thuStart = enabled
                        ? (scheduleForm.thuStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const thuEnd = enabled
                        ? (scheduleForm.thuEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        thuStart,
                        thuEnd,
                        schedule: toggleDayEnabled(THU_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Fri'}
                isEnabled={dayIsEnabled(FRI_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.friStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        friStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.friEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        friEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(FRI_SCHEDULE, scheduleForm.schedule)
                    const friStart = enabled
                        ? (scheduleForm.friStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const friEnd = enabled
                        ? (scheduleForm.friEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        friStart,
                        friEnd,
                        schedule: toggleDayEnabled(FRI_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Sat'}
                isEnabled={dayIsEnabled(SAT_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.satStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        satStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.satEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        satEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(SAT_SCHEDULE, scheduleForm.schedule)
                    const satStart = enabled
                        ? (scheduleForm.satStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const satEnd = enabled
                        ? (scheduleForm.satEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        satStart,
                        satEnd,
                        schedule: toggleDayEnabled(SAT_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
            <SingleDaySchedule
                day={'Sun'}
                isEnabled={dayIsEnabled(SUN_SCHEDULE, scheduleForm.schedule)}
                start={getStartHourOption(scheduleForm.sunStart)}
                onStartChange={(start) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        sunStart: start.value ? Number(start.value) : undefined,
                    })
                }}
                end={getEndHourOption(scheduleForm.sunEnd)}
                onEndChange={(end) => {
                    onUpdateScheduleForm({
                        ...scheduleForm,
                        sunEnd: end.value ? Number(end.value) : undefined,
                    })
                }}
                onToggle={() => {
                    const enabled = !dayIsEnabled(SUN_SCHEDULE, scheduleForm.schedule)
                    const sunStart = enabled
                        ? (scheduleForm.sunStart ?? Number(DEFAULT_START.value))
                        : undefined
                    const sunEnd = enabled
                        ? (scheduleForm.sunEnd ?? Number(DEFAULT_END.value))
                        : undefined

                    onUpdateScheduleForm({
                        ...scheduleForm,
                        sunStart,
                        sunEnd,
                        schedule: toggleDayEnabled(SUN_SCHEDULE, scheduleForm.schedule, enabled),
                    })
                }}
            />
        </form>
    )
}
